// src/components/chemistry/StatsPanel.tsx — Statistical analysis panel for chart data
import React, { useMemo, useState } from 'react';
import {
  descriptiveStats, checkNormality, independentTTest, mannWhitneyU,
  oneWayAnova, tukeyHSD, dunnsTest, benjaminiHochberg,
  significanceStars, effectSizeLabel, formatP,
  type DescriptiveStats, type PostHocResult,
} from '../../utils/statistics';

interface GroupData {
  name: string;
  data: number[];
}

interface StatsPanelProps {
  groups: GroupData[];
  column: string;
}

const MONO = "'SF Mono', 'Cascadia Code', 'Consolas', monospace";

const cellStyle: React.CSSProperties = {
  padding: '3px 8px',
  fontSize: '11px',
  fontFamily: MONO,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};

const headerCellStyle: React.CSSProperties = {
  ...cellStyle,
  color: 'var(--text-muted)',
  fontWeight: 600,
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: '1px solid var(--card-border)',
};

const cardStyle: React.CSSProperties = {
  background: 'var(--hover-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '6px',
  padding: '10px 12px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '10px',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '6px',
};

const StatsPanel: React.FC<StatsPanelProps> = ({ groups, column }) => {
  const [correction, setCorrection] = useState<'bonferroni' | 'bh'>('bonferroni');

  // Compute all stats
  const groupStats = useMemo(() =>
    groups.map(g => ({
      name: g.name,
      stats: descriptiveStats(g.data),
      normality: descriptiveStats(g.data) ? checkNormality(descriptiveStats(g.data)!) : null,
    })).filter(g => g.stats !== null),
  [groups]);

  const validGroups = useMemo(() =>
    groups.filter(g => {
      const clean = g.data.filter(d => !isNaN(d));
      return clean.length >= 2;
    }),
  [groups]);

  const nGroups = validGroups.length;

  // 2-group tests
  const tTest = useMemo(() =>
    nGroups === 2 ? independentTTest(validGroups[0].data, validGroups[1].data) : null,
  [nGroups, validGroups]);

  const mwu = useMemo(() =>
    nGroups === 2 ? mannWhitneyU(validGroups[0].data, validGroups[1].data) : null,
  [nGroups, validGroups]);

  // 3+ group tests
  const anova = useMemo(() =>
    nGroups >= 3 ? oneWayAnova(validGroups) : null,
  [nGroups, validGroups]);

  const postHocParametric = useMemo((): PostHocResult | null =>
    anova?.significant ? tukeyHSD(validGroups) : null,
  [anova, validGroups]);

  const postHocNonParametric = useMemo((): PostHocResult | null =>
    anova?.significant ? dunnsTest(validGroups) : null,
  [anova, validGroups]);

  // Apply BH correction to post-hoc if selected
  const adjustPostHoc = (result: PostHocResult | null): PostHocResult | null => {
    if (!result) return null;
    if (correction === 'bonferroni') return result; // already Bonferroni
    // Re-compute with BH
    const rawPs = result.pairs.map(p => p.pValue);
    const adjusted = benjaminiHochberg(rawPs);
    return {
      method: result.method.replace('Bonferroni', 'Benjamini-Hochberg'),
      pairs: result.pairs.map((p, i) => ({
        ...p,
        pAdjusted: adjusted[i],
        significant: adjusted[i] < 0.05,
      })),
    };
  };

  if (groupStats.length === 0) {
    return (
      <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '12px' }}>
        Not enough data for statistical analysis.
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--card-bg)',
      borderTop: '1px solid var(--card-border)',
      padding: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      maxHeight: '380px',
      overflowY: 'auto',
    }}>
      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>
          Statistical Analysis: {column}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
          {groupStats.length} groups, {groupStats.reduce((s, g) => s + (g.stats?.n || 0), 0)} total observations
        </span>
      </div>

      {/* Descriptive Stats Table */}
      <div style={cardStyle}>
        <div style={labelStyle}>Descriptive Statistics</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {['Group', 'n', 'Mean \u00B1 SEM', 'Median', 'Std', 'Q1', 'Q3', 'Min', 'Max', 'Normality'].map(h => (
                  <th key={h} style={headerCellStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupStats.map(g => (
                <tr key={g.name} style={{ borderBottom: '1px solid var(--divider)' }}>
                  <td style={{ ...cellStyle, color: 'var(--text-primary)', fontWeight: 500 }}>{g.name}</td>
                  <td style={cellStyle}>{g.stats!.n}</td>
                  <td style={cellStyle}>{g.stats!.mean.toFixed(2)} \u00B1 {g.stats!.sem.toFixed(2)}</td>
                  <td style={cellStyle}>{g.stats!.median.toFixed(2)}</td>
                  <td style={cellStyle}>{g.stats!.std.toFixed(2)}</td>
                  <td style={cellStyle}>{g.stats!.q1.toFixed(2)}</td>
                  <td style={cellStyle}>{g.stats!.q3.toFixed(2)}</td>
                  <td style={cellStyle}>{g.stats!.min.toFixed(2)}</td>
                  <td style={cellStyle}>{g.stats!.max.toFixed(2)}</td>
                  <td style={{
                    ...cellStyle,
                    color: g.normality?.isApproxNormal ? '#22c55e' : '#f59e0b',
                  }}>
                    {g.normality?.isApproxNormal ? '\u2713' : '\u26A0'} {g.normality?.reason || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 2-group comparison */}
      {nGroups === 2 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {/* Welch's t-test */}
          <div style={cardStyle}>
            <div style={labelStyle}>Welch's t-test</div>
            {tTest ? (
              <div style={{ fontSize: '12px', fontFamily: MONO, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <div>
                  t = {tTest.t.toFixed(3)}, df = {tTest.df.toFixed(1)}
                </div>
                <div>
                  p = {formatP(tTest.pValue)}{' '}
                  <span style={{ color: tTest.significant ? '#22c55e' : 'var(--text-muted)', fontWeight: 700 }}>
                    {significanceStars(tTest.pValue)}
                  </span>
                </div>
                <div>
                  Mean diff: {tTest.meanDiff.toFixed(3)} [{tTest.ci95[0].toFixed(3)}, {tTest.ci95[1].toFixed(3)}]
                </div>
                <div>
                  Cohen's d: {tTest.effectSize.toFixed(2)} ({effectSizeLabel(tTest.effectSize)})
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Insufficient data (n &lt; 2 per group)</div>
            )}
          </div>

          {/* Mann-Whitney U */}
          <div style={cardStyle}>
            <div style={labelStyle}>Mann-Whitney U</div>
            {mwu ? (
              <div style={{ fontSize: '12px', fontFamily: MONO, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <div>U = {mwu.U.toFixed(0)}, z = {mwu.z.toFixed(3)}</div>
                <div>
                  p = {formatP(mwu.pValue)}{' '}
                  <span style={{ color: mwu.significant ? '#22c55e' : 'var(--text-muted)', fontWeight: 700 }}>
                    {significanceStars(mwu.pValue)}
                  </span>
                </div>
                <div>n1 = {mwu.n1}, n2 = {mwu.n2}</div>
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Insufficient data</div>
            )}
          </div>

          {/* Guidance note */}
          <div style={{
            gridColumn: '1 / -1',
            fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic',
            padding: '4px 0',
          }}>
            Welch's t-test assumes normality. Mann-Whitney U is non-parametric (no normality assumption).
            Use Mann-Whitney if data is non-normal or n &lt; 20.
          </div>
        </div>
      )}

      {/* 3+ group comparison */}
      {nGroups >= 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* ANOVA */}
          <div style={cardStyle}>
            <div style={labelStyle}>One-way ANOVA</div>
            {anova ? (
              <div style={{ fontSize: '12px', fontFamily: MONO, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <div>
                  F({anova.dfBetween}, {anova.dfWithin}) = {anova.F.toFixed(3)}
                </div>
                <div>
                  p = {formatP(anova.pValue)}{' '}
                  <span style={{ color: anova.significant ? '#22c55e' : 'var(--text-muted)', fontWeight: 700 }}>
                    {significanceStars(anova.pValue)}
                  </span>
                </div>
                <div>
                  {'\u03B7\u00B2'} = {anova.etaSquared.toFixed(3)} ({effectSizeLabel(Math.sqrt(anova.etaSquared / (1 - anova.etaSquared)) * 2)})
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Insufficient data for ANOVA</div>
            )}
          </div>

          {/* Post-hoc tests (only if ANOVA significant) */}
          {anova?.significant && (
            <>
              {/* Correction toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>p-value correction:</span>
                <div style={{
                  display: 'flex', background: 'var(--card-bg)', borderRadius: '4px',
                  border: '1px solid var(--card-border)', overflow: 'hidden',
                }}>
                  {(['bonferroni', 'bh'] as const).map(c => (
                    <button
                      key={c}
                      onClick={() => setCorrection(c)}
                      style={{
                        padding: '2px 8px', border: 'none', fontSize: '10px',
                        background: correction === c ? 'rgba(29, 185, 84, 0.2)' : 'transparent',
                        color: correction === c ? '#1db954' : 'var(--text-muted)',
                        cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
                      }}
                    >
                      {c === 'bonferroni' ? 'Bonferroni' : 'Benjamini-Hochberg'}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {/* Tukey's HSD */}
                <PostHocTable result={adjustPostHoc(postHocParametric)} title="Tukey's HSD (parametric)" />
                {/* Dunn's test */}
                <PostHocTable result={adjustPostHoc(postHocNonParametric)} title="Dunn's test (non-parametric)" />
              </div>

              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                ANOVA assumes normality and equal variances. Use Dunn's test if data is non-normal.
                Post-hoc tests identify which specific groups differ.
                Significance: * p&lt;0.05, ** p&lt;0.01, *** p&lt;0.001
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Sub-component for post-hoc tables
const PostHocTable: React.FC<{ result: PostHocResult | null; title: string }> = ({ result, title }) => {
  if (!result) return null;
  return (
    <div style={cardStyle}>
      <div style={labelStyle}>{title}</div>
      <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
        {result.pairs.map((pair, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '2px 0', fontSize: '11px', fontFamily: MONO,
            borderBottom: i < result.pairs.length - 1 ? '1px solid var(--divider)' : 'none',
          }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              {pair.group1} vs {pair.group2}
            </span>
            <span style={{
              color: pair.significant ? '#22c55e' : 'var(--text-muted)',
              fontWeight: pair.significant ? 700 : 400,
            }}>
              p={pair.pAdjusted < 0.001 ? '<.001' : pair.pAdjusted.toFixed(3)} {significanceStars(pair.pAdjusted)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsPanel;

// Helper: build significance bracket annotations for Plotly
export function buildSignificanceBrackets(
  postHoc: PostHocResult | null,
  groupNames: string[],
): any[] {
  if (!postHoc) return [];

  const sigPairs = postHoc.pairs.filter(p => p.significant);
  if (sigPairs.length === 0) return [];

  const annotations: any[] = [];
  const shapes: any[] = [];

  // Sort by span width (narrower first)
  const sorted = sigPairs.sort((a, b) => {
    const spanA = Math.abs(groupNames.indexOf(a.group1) - groupNames.indexOf(a.group2));
    const spanB = Math.abs(groupNames.indexOf(b.group1) - groupNames.indexOf(b.group2));
    return spanA - spanB;
  });

  sorted.forEach((pair, layerIdx) => {
    const i1 = groupNames.indexOf(pair.group1);
    const i2 = groupNames.indexOf(pair.group2);
    if (i1 === -1 || i2 === -1) return;

    const yOffset = 1.02 + layerIdx * 0.06;
    const stars = significanceStars(pair.pAdjusted);

    // Horizontal bracket line
    shapes.push({
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: Math.min(i1, i2),
      x1: Math.max(i1, i2),
      y0: yOffset,
      y1: yOffset,
      line: { color: '#888', width: 1 },
    });

    // Left leg
    shapes.push({
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: Math.min(i1, i2),
      x1: Math.min(i1, i2),
      y0: yOffset - 0.015,
      y1: yOffset,
      line: { color: '#888', width: 1 },
    });

    // Right leg
    shapes.push({
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: Math.max(i1, i2),
      x1: Math.max(i1, i2),
      y0: yOffset - 0.015,
      y1: yOffset,
      line: { color: '#888', width: 1 },
    });

    // Stars annotation
    annotations.push({
      x: (Math.min(i1, i2) + Math.max(i1, i2)) / 2,
      y: yOffset + 0.01,
      xref: 'x',
      yref: 'paper',
      text: stars,
      showarrow: false,
      font: { size: 12, color: stars === 'ns' ? '#999' : (document.body.classList.contains('theme-dark') ? '#e0e0e0' : '#111827') },
    });
  });

  return [annotations, shapes];
}
